import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { AlertTriangle, ImageUp, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import apiClient from "../api-client";
import { Button } from "./Button";
import { useAuth } from "./contexts/AuthContext.jsx";
import markezyLogo from "../../markezy-login-logo.png";
import UnsavedChangesBar from "./UnsavedChangesBar";
import {
  DEFAULT_INK,
  DEFAULT_PRIMARY,
  DEFAULT_SECONDARY,
  DEFAULT_TOKENS,
  contrastWithWhite,
  deriveBrandPalette,
  isHexColor,
} from "../theme/brandPalette.js";

const MAX_LOGO_BYTES = 2 * 1024 * 1024;
const LOGO_TYPES = ["image/png", "image/jpeg", "image/webp"];

const ColorField = ({ id, label, help, value, defaultValue, onChange, disabled = false }) => {
  const theme = useTheme();
  const [draft, setDraft] = useState(value || "");
  const effective = value || defaultValue;

  useEffect(() => setDraft(value || ""), [value]);

  const commitDraft = (raw) => {
    const next = raw.startsWith("#") ? raw : `#${raw}`;
    setDraft(raw);
    if (raw === "") onChange("");
    else if (isHexColor(next)) onChange(next.toUpperCase());
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
      <Typography component="label" htmlFor={id} sx={{ fontSize: "0.875rem", fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: "0.8125rem" }}>
        {help}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box
          component="input"
          type="color"
          disabled={disabled}
          aria-label={`${label}: selector`}
          value={effective}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          sx={{
            width: 40,
            height: 40,
            p: 0,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "8px",
            cursor: "pointer",
            backgroundColor: "transparent",
          }}
        />
        <Box
          component="input"
          disabled={disabled}
          id={id}
          value={draft}
          placeholder={`${defaultValue} (por defecto)`}
          onChange={(e) => commitDraft(e.target.value.trim())}
          maxLength={7}
          sx={{
            width: 170,
            height: 40,
            px: 1.5,
            fontFamily: "monospace",
            fontSize: "0.875rem",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "8px",
            outline: "none",
            "&:focus": { borderColor: theme.palette.text.primary },
          }}
        />
        {value && (
          <Button variant="ghost" size="sm" disabled={disabled} onClick={() => onChange("")} sx={{ gap: 0.5 }}>
            <RotateCcw size={14} /> Por defecto
          </Button>
        )}
      </Box>
    </Box>
  );
};

ColorField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  help: PropTypes.string.isRequired,
  value: PropTypes.string,
  defaultValue: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

// Muestra en pequeño cómo quedarán los elementos principales con la paleta y el
// tamaño de letra de las conversaciones elegidos
export const BrandPreview = ({ primary, secondary, logoSrc = markezyLogo, fontSize }) => {
  const theme = useTheme();
  const palette = deriveBrandPalette({ primary_color: primary, secondary_color: secondary });
  const t = palette?.tokens || DEFAULT_TOKENS;
  const inkFill = palette?.inkFill || DEFAULT_INK;
  const inkFillText = palette?.inkFillText || "#FFFFFF";
  const avatar = palette?.avatar || DEFAULT_INK;
  const contrast = palette?.primaryContrastText || "#FFFFFF";

  return (
    <Box
      aria-label="Vista previa de la marca"
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: "12px",
        overflow: "hidden",
        display: "flex",
        minHeight: 180,
      }}
    >
      <Box sx={{ width: 150, borderRight: `1px solid ${theme.palette.divider}`, p: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
        <Box component="img" src={logoSrc} alt="" sx={{ height: 28, maxWidth: "100%", objectFit: "contain", alignSelf: "flex-start" }} />
        <Box sx={{ px: 1, py: 0.75, borderRadius: "8px", backgroundColor: t.soft, color: t.pink, fontSize: "0.75rem", fontWeight: 600 }}>
          Clientes
        </Box>
        <Box sx={{ px: 1, py: 0.75, fontSize: "0.75rem", color: theme.palette.text.secondary }}>Proyectos</Box>
      </Box>
      <Box sx={{ flex: 1, p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          <Box sx={{ px: 2, py: 0.75, borderRadius: 999, background: palette ? t.pink : `linear-gradient(95deg, ${t.pink} 0%, ${t.orange} 100%)`, color: contrast, fontSize: "0.75rem", fontWeight: 600 }}>
            Botón principal
          </Box>
          <Box sx={{ px: 2, py: 0.75, borderRadius: 999, backgroundColor: inkFill, color: inkFillText, fontSize: "0.75rem", fontWeight: 600 }}>
            Secundario
          </Box>
          <Box sx={{ width: 28, height: 28, borderRadius: "50%", backgroundColor: avatar, color: "#fff", fontSize: "0.6875rem", display: "grid", placeItems: "center", fontWeight: 600 }}>
            AB
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "flex-end", gap: 0.75, height: 64 }}>
          {[40, 70, 55, 90, 65, 80].map((h, i) => (
            <Box key={i} sx={{ width: 16, height: `${h}%`, borderRadius: "4px 4px 0 0", backgroundColor: i % 2 ? t.orange : t.pink }} />
          ))}
        </Box>
        {fontSize && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, lineHeight: 1.5 }}>
            <Typography sx={{ fontSize: `${fontSize}px`, color: theme.palette.text.primary }}>
              Este es un mensaje de ejemplo en el muro de conversaciones.
            </Typography>
            <Typography sx={{ fontSize: `${fontSize}px`, color: theme.palette.text.secondary, pl: 2, borderLeft: `2px solid ${t.softBorder}` }}>
              Y este es un comentario de respuesta.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

BrandPreview.propTypes = {
  primary: PropTypes.string,
  secondary: PropTypes.string,
  logoSrc: PropTypes.string,
  fontSize: PropTypes.number,
};

/**
 * Configuración de marca blanca de una agencia: logotipo, color primario y secundario.
 * Lo usan el admin de agencia (su propia agencia) y el superadmin (cualquiera).
 */
export const AgencyBrandingSettings = ({
  agencyId,
  onSaved,
  fontSize,
  extraDirty = false,
  onSaveExtra,
  onDiscardExtra,
  floatingSave = false,
}) => {
  const theme = useTheme();
  const { user, refreshUser } = useAuth();
  const brandingLocked = user?.role !== "superadmin" && user?.agencyFeatures?.white_label === false;
  const fileInputRef = useRef(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState({ logo_url: null, primary_color: "", secondary_color: "" });
  const [primary, setPrimary] = useState("");
  const [secondary, setSecondary] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [logoRemoved, setLogoRemoved] = useState(false);

  const applyServerBranding = (branding = {}) => {
    setSaved({
      logo_url: branding.logo_url || null,
      primary_color: branding.primary_color || "",
      secondary_color: branding.secondary_color || "",
    });
    setPrimary(branding.primary_color || "");
    setSecondary(branding.secondary_color || "");
    setLogoFile(null);
    setLogoRemoved(false);
  };

  useEffect(() => {
    if (!agencyId) return undefined;
    let cancelled = false;
    setIsLoading(true);
    apiClient
      .get(`agencies/${agencyId}/`)
      .then(({ data }) => {
        if (!cancelled) applyServerBranding(data?.branding_config || data?.branding);
      })
      .catch(() => !cancelled && toast.error("No se pudo cargar la marca de la agencia."))
      .finally(() => !cancelled && setIsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [agencyId]);

  const logoPreviewUrl = useMemo(() => (logoFile ? URL.createObjectURL(logoFile) : null), [logoFile]);
  useEffect(() => () => logoPreviewUrl && URL.revokeObjectURL(logoPreviewUrl), [logoPreviewUrl]);

  const currentLogo = logoPreviewUrl || (!logoRemoved && saved.logo_url) || null;
  const isBrandingDirty =
    !!logoFile || logoRemoved || primary !== saved.primary_color || secondary !== saved.secondary_color;
  const lowContrast = primary && contrastWithWhite(primary) < 3;

  const handleFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!LOGO_TYPES.includes(file.type)) {
      toast.error("Formato no soportado. Usa PNG, JPG o WEBP.");
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast.error("El logotipo no puede superar los 2 MB.");
      return;
    }
    setLogoFile(file);
    setLogoRemoved(false);
  };

  const isDirty = isBrandingDirty || extraDirty;
  const changeCount = [
    logoFile || logoRemoved,
    primary !== saved.primary_color,
    secondary !== saved.secondary_color,
    extraDirty,
  ].filter(Boolean).length;

  const handleReset = () => {
    applyServerBranding(saved);
    if (extraDirty) onDiscardExtra?.();
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (extraDirty) await onSaveExtra?.();
      if (!isBrandingDirty) return;
      let response;
      if (logoFile) {
        const form = new FormData();
        form.append("brand_logo", logoFile);
        form.append("brand_primary_color", primary);
        form.append("brand_secondary_color", secondary);
        response = await apiClient.patch(`agencies/${agencyId}/`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        response = await apiClient.patch(`agencies/${agencyId}/`, {
          brand_primary_color: primary,
          brand_secondary_color: secondary,
          ...(logoRemoved && { brand_logo: null }),
        });
      }
      applyServerBranding(response?.data?.branding);
      toast.success("Marca actualizada correctamente.");
      onSaved?.();
      // La marca propia se aplica al recargar el perfil (vuelve a montar la app con los colores nuevos)
      if (user?.agencyId === agencyId) await refreshUser();
    } catch (error) {
      const data = error?.response?.data || {};
      const message =
        data.brand_logo?.[0] || data.brand_primary_color?.[0] || data.brand_secondary_color?.[0] ||
        "No se pudo guardar la marca.";
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size={18} />
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          Cargando marca...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {brandingLocked && <Alert severity="info">Disponible próximamente en planes de pago. Tu configuración se conserva y puedes previsualizarla.</Alert>}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography sx={{ fontSize: "0.875rem", fontWeight: 600 }}>Logotipo</Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: "0.8125rem" }}>
          Aparece en la cabecera de la plataforma para todo tu equipo y tus clientes, y en los informes.
          PNG, JPG o WEBP de hasta 2 MB; mejor horizontal y con fondo transparente.
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <Box
            sx={{
              width: 200,
              height: 72,
              border: `1px dashed ${theme.palette.divider}`,
              borderRadius: "8px",
              p: 1,
              boxSizing: "border-box",
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Box
              component="img"
              src={currentLogo || markezyLogo}
              alt={currentLogo ? "Logotipo de la agencia" : "Logotipo por defecto"}
              sx={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
            />
          </Box>
          <input ref={fileInputRef} type="file" accept={LOGO_TYPES.join(",")} hidden onChange={handleFile} disabled={brandingLocked} />
          <Button variant="outline" size="sm" disabled={brandingLocked} onClick={() => fileInputRef.current?.click()} sx={{ gap: 0.75 }}>
            <ImageUp size={16} /> {currentLogo ? "Cambiar logotipo" : "Subir logotipo"}
          </Button>
          {currentLogo && (
            <Button
              variant="ghost"
              size="sm"
              disabled={brandingLocked}
              onClick={() => {
                setLogoFile(null);
                setLogoRemoved(!!saved.logo_url);
              }}
              sx={{ gap: 0.75 }}
            >
              <Trash2 size={16} /> Usar el logotipo por defecto
            </Button>
          )}
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <ColorField
          id="brand-primary-color"
          label="Color primario"
          help="Botones principales, avatares, menú lateral y enlaces."
          value={primary}
          defaultValue={DEFAULT_PRIMARY}
          onChange={setPrimary}
          disabled={brandingLocked}
        />
        <ColorField
          id="brand-secondary-color"
          label="Color secundario"
          help="Fondos al pasar el ratón, gráficas, degradados y botones oscuros."
          value={secondary}
          defaultValue={DEFAULT_SECONDARY}
          onChange={setSecondary}
          disabled={brandingLocked}
        />
      </Box>

      {lowContrast && (
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", color: theme.palette.warning.dark, fontSize: "0.8125rem" }}>
          <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
          El color primario es muy claro: el texto de los botones se mostrará en oscuro y los
          enlaces pueden leerse mal sobre fondo blanco.
        </Box>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography sx={{ fontSize: "0.875rem", fontWeight: 600 }}>Vista previa</Typography>
        <BrandPreview primary={primary} secondary={secondary} logoSrc={currentLogo || markezyLogo} fontSize={fontSize} />
      </Box>

      {floatingSave ? (
        <UnsavedChangesBar count={changeCount} saving={isSaving} onDiscard={handleReset} onSave={handleSave} />
      ) : (
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button variant="outline" onClick={handleReset} disabled={brandingLocked || !isDirty || isSaving}>
            Descartar cambios
          </Button>
          <Button onClick={handleSave} disabled={brandingLocked || !isDirty || isSaving}>
            {isSaving ? "Guardando..." : "Guardar cambios"}
          </Button>
        </Box>
      )}
    </Box>
  );
};

AgencyBrandingSettings.propTypes = {
  agencyId: PropTypes.number,
  onSaved: PropTypes.func,
  fontSize: PropTypes.number,
  // Cambios ajenos a la marca (p. ej. tamaño de letra) que se guardan con el mismo botón
  extraDirty: PropTypes.bool,
  onSaveExtra: PropTypes.func,
  onDiscardExtra: PropTypes.func,
  // En página usa la barra flotante; dentro de un modal, los botones de siempre
  floatingSave: PropTypes.bool,
};
